import {
  CreditCardIcon,
  HouseIcon,
  PiggyBankIcon,
  ReceiptIcon,
  SealCheckIcon,
  SpinnerGapIcon,
  WalletIcon,
} from '@phosphor-icons/react'
import { type PlainDate, differenceInDays, formatMoney, todayIn } from '@paybae/shared'
import { type ReactNode, useState } from 'react'
import { useNavigate } from 'react-router'

import { Card, EmptyState, SectionHeader } from '@/components/Card'
import { ProgressRing } from '@/components/Donut'
import { ErrorState, Skeleton } from '@/components/Estado'
import { AvisoError, Campo, FormSheet, Guardar, Monto } from '@/components/Form'
import { Money } from '@/components/Money'
import { ScreenHeader } from '@/components/ScreenHeader'
import type { FijoPendiente, ReconciliationSummary } from '@/lib/api'
import { cn } from '@/lib/cn'
import { haptic } from '@/lib/haptics'
import { useJustificacion, useMarcarFijoPagado, useRecurring } from '@/lib/queries'
import { useSession } from '@/lib/session'
// El detalle por campo que manda el servidor, ya desempaquetado. Vive en la
// pantalla de fijos porque es donde nació; se reutiliza en vez de copiarlo.
import { mensajeDeError } from '@/routes/Recurring'

/**
 * Justificación del dinero (§12).
 *
 * Responde una sola pregunta: **entraron veinte mil, quedan diez mil, ¿dónde
 * están los otros diez?** No acusa a nadie de gastar de más —eso sería un juicio
 * y la app no lo hace—: pone el porcentaje ubicado y ofrece la lista de gastos
 * fijos sin registrar, que es donde casi siempre está el dinero «perdido».
 *
 * El anillo cuenta como ubicado lo registrado **más el saldo que sigue en las
 * cuentas**: el dinero que no se ha gastado está localizado, no desaparecido.
 * Por eso el desglose incluye una cuarta fila con ese saldo — sin ella la suma
 * no cuadraría con el anillo y la pantalla parecería estar mintiendo.
 */
export function Justificar() {
  const navigate = useNavigate()
  const { membership } = useSession()
  const hoy = todayIn(membership.household.timezone)

  const justificacion = useJustificacion()
  const marcar = useMarcarFijoPagado()

  // Consulta secundaria: solo sirve para saber si un recibo cambia de importe
  // cada mes. Si falla, marcar sigue funcionando —el servidor responde pidiendo
  // el monto— así que no bloquea la pantalla.
  const fijos = useRecurring()

  /** Recibo de importe variable cuyo monto real se está pidiendo. */
  const [pidiendoMonto, setPidiendoMonto] = useState<FijoPendiente | null>(null)

  if (justificacion.isPending) return <Cargando />

  if (justificacion.isError || !justificacion.data) {
    return (
      <>
        <ScreenHeader title="¿En qué se fue tu dinero?" tinte="peach" />
        <ErrorState
          titulo="No pudimos cuadrar tu dinero"
          mensaje={
            justificacion.error instanceof Error
              ? justificacion.error.message
              : 'Inténtalo de nuevo.'
          }
          onReintentar={() => void justificacion.refetch()}
        />
      </>
    )
  }

  const data = justificacion.data
  const todoUbicado = data.sinJustificarCents === 0

  const marcarPagado = (pendiente: FijoPendiente): void => {
    // Un recibo que cambia cada periodo no se puede dar por pagado de un toque:
    // el servidor exige el importe real y respondería 400. Se pide antes.
    if (esVariable(fijos.data, pendiente.id)) {
      haptic('light')
      setPidiendoMonto(pendiente)
      return
    }
    haptic('light')
    marcar.mutate(
      { recurringExpenseId: pendiente.id },
      { onSuccess: () => haptic('success'), onError: () => haptic('error') },
    )
  }

  return (
    <>
      <ScreenHeader title="¿En qué se fue tu dinero?" subtitle={etiquetaPeriodo(data.periodo)} tinte="peach" />

      <div className="px-safe mx-auto flex max-w-lg flex-col gap-4 pt-1">
        <Anillo data={data} todoUbicado={todoUbicado} onVolver={() => navigate('/')} />

        <section>
          <SectionHeader title="Dónde está lo que ya ubicamos" />
          <Card className="divide-y divide-line p-0">
            <FilaConcepto
              icono={<ReceiptIcon size={18} weight="fill" />}
              titulo="Gastos registrados"
              detalle="Lo que salió de tus cuentas y ya tiene categoría"
              cents={data.desglose.gastosCents}
            />
            <FilaConcepto
              icono={<PiggyBankIcon size={18} weight="fill" />}
              titulo="Ahorro apartado"
              detalle="Aportaciones a fondos y traspasos a tus cuentas de ahorro"
              cents={data.desglose.ahorroCents}
            />
            <FilaConcepto
              icono={<CreditCardIcon size={18} weight="fill" />}
              titulo="Pagos de tarjeta"
              detalle="El día que el dinero se fue de la cuenta, no el de la compra"
              cents={data.desglose.pagosTarjetaCents}
            />
            {/* Cuarta fila, y no un dato aparte: sin ella el desglose sumaría
                menos que el anillo y parecería que faltan cifras. */}
            <FilaConcepto
              icono={<WalletIcon size={18} weight="fill" />}
              titulo="Sigue en tus cuentas"
              detalle="No se ha gastado: está localizado"
              cents={data.saldoActualCents}
            />
          </Card>
        </section>

        {marcar.isError ? <AvisoError mensaje={mensajeDeError(marcar.error)} /> : null}

        <section className="pb-2">
          <SectionHeader title="Gastos fijos sin registrar" />
          {data.fijosPendientes.length === 0 ? (
            <Card>
              {todoUbicado ? (
                <EmptyState
                  title="Nada pendiente de este periodo"
                  description="Todos tus gastos fijos que vencían ya quedaron registrados."
                />
              ) : (
                <EmptyState
                  icon={<ReceiptIcon size={30} weight="fill" />}
                  title="No queda ningún fijo por marcar"
                  description="Lo que falta por clasificar salió en algún gasto suelto que todavía no capturas. Regístralo y el porcentaje sube solo."
                  action={
                    <button
                      type="button"
                      onClick={() => {
                        haptic('light')
                        navigate('/capturar')
                      }}
                      className="flex min-h-[44px] items-center rounded-pill bg-accion px-5 text-[14px] font-semibold text-on-accion active:scale-95"
                    >
                      Registrar un gasto
                    </button>
                  }
                />
              )}
            </Card>
          ) : (
            <>
              <Card className="divide-y divide-line p-0">
                {data.fijosPendientes.map((pendiente) => (
                  <FilaPendiente
                    key={pendiente.id}
                    pendiente={pendiente}
                    hoy={hoy}
                    variable={esVariable(fijos.data, pendiente.id)}
                    guardando={
                      marcar.isPending && marcar.variables?.recurringExpenseId === pendiente.id
                    }
                    onMarcar={() => marcarPagado(pendiente)}
                  />
                ))}
              </Card>
              <p className="mt-2 px-1 text-[12px] leading-snug text-ink-faint">
                Marcarlos registra el cargo y baja el saldo de la cuenta de donde salen, igual que
                si lo capturaras a mano.
              </p>
            </>
          )}
        </section>
      </div>

      {pidiendoMonto ? (
        <HojaMontoReal pendiente={pidiendoMonto} onCerrar={() => setPidiendoMonto(null)} />
      ) : null}
    </>
  )
}

/**
 * El anillo y su lectura.
 *
 * El centro lleva lo que falta por clasificar, no el porcentaje: el porcentaje
 * ya lo dice el arco, y repetirlo desperdiciaría el único hueco donde cabe una
 * cifra en pesos —que es sobre la que se puede hacer algo—. Va en tinta aunque
 * sea «lo malo»: teñir de rojo un dinero que casi siempre está en un recibo sin
 * capturar entrenaría a temer la pantalla.
 */
function Anillo({
  data,
  todoUbicado,
  onVolver,
}: {
  data: ReconciliationSummary
  todoUbicado: boolean
  onVolver: () => void
}) {
  return (
    <Card className="flex flex-col items-center gap-4">
      <ProgressRing
        // Con todo ubicado el anillo va lleno. El porcentaje del servidor es 0
        // cuando en el periodo no ha entrado nada (no hay de qué sacar
        // porcentaje), y el anillo salía vacío —un punto verde suelto— junto a
        // «todo ubicado»: la figura y la frase decían lo contrario.
        percent={todoUbicado ? 100 : data.porcentajeIdentificado}
        size={196}
        stroke={16}
        color={todoUbicado ? 'var(--income)' : 'var(--primary)'}
      >
        <div className="flex flex-col items-center px-8 text-center">
          {todoUbicado ? (
            <>
              <SealCheckIcon size={38} weight="fill" className="text-income-text" />
              <span className="mt-1.5 text-[12px] font-medium leading-tight text-ink-soft">
                todo ubicado
              </span>
            </>
          ) : (
            <>
              <span
                data-money
                className="font-display text-[30px] font-extrabold leading-none tracking-[-0.04em] text-ink"
              >
                {formatMoney(data.sinJustificarCents, { hideZeroCents: true })}
              </span>
              <span className="mt-1.5 text-[12px] font-medium leading-tight text-ink-soft">
                sin clasificar
              </span>
            </>
          )}
        </div>
      </ProgressRing>

      {todoUbicado ? (
        <>
          <p className="text-center text-[15px] font-semibold leading-snug text-ink">
            Cada peso que entró este periodo está ubicado.
          </p>
          <button
            type="button"
            onClick={() => {
              haptic('light')
              onVolver()
            }}
            className="flex min-h-[44px] items-center gap-2 rounded-pill border border-line bg-surface px-5 text-[14px] font-semibold text-ink active:scale-95"
          >
            <HouseIcon size={16} weight="fill" />
            Volver al inicio
          </button>
        </>
      ) : (
        <p className="text-center text-[13px] leading-snug text-ink-soft">
          Ubicamos{' '}
          <strong className="font-semibold text-ink">
            {Math.round(data.porcentajeIdentificado)}%
          </strong>{' '}
          de los{' '}
          <strong className="font-semibold text-ink" data-money>
            {formatMoney(data.recibidoCents, { hideZeroCents: true })}
          </strong>{' '}
          que recibiste este periodo.
        </p>
      )}
    </Card>
  )
}

function FilaConcepto({
  icono,
  titulo,
  detalle,
  cents,
}: {
  icono: ReactNode
  titulo: string
  detalle: string
  cents: number
}) {
  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <span
        className={cn(
          'flex size-10 shrink-0 items-center justify-center rounded-full',
          // Sin acento: estas cuatro filas son partes de un mismo total, y darle
          // color a una la haría parecer más importante que las otras.
          'bg-surface-sunken text-ink-soft',
        )}
      >
        {icono}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[14px] font-medium text-ink">{titulo}</p>
        <p className="text-[12px] leading-snug text-ink-soft">{detalle}</p>
      </div>
      <Money cents={cents} className="shrink-0 text-[15px]" tone={cents === 0 ? 'soft' : 'ink'} />
    </div>
  )
}

function FilaPendiente({
  pendiente,
  hoy,
  variable,
  guardando,
  onMarcar,
}: {
  pendiente: FijoPendiente
  hoy: PlainDate
  variable: boolean
  guardando: boolean
  onMarcar: () => void
}) {
  const dias = differenceInDays(pendiente.vencimiento, hoy)

  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <div className="min-w-0 flex-1">
        <p className="truncate text-[14px] font-medium text-ink">{pendiente.nombre}</p>
        <p className="truncate text-[12px] text-ink-soft">{textoVencimiento(dias)}</p>
        <p className="mt-0.5 flex items-baseline gap-1">
          {/* El estimado se anuncia como tal: darlo por cerrado haría cuadrar
              cuentas contra una cifra que todavía no existe. */}
          {variable ? <span className="text-[11px] text-ink-faint">aprox.</span> : null}
          <Money cents={pendiente.montoCents} className="text-[14px]" />
        </p>
      </div>

      <button
        type="button"
        onClick={onMarcar}
        disabled={guardando}
        className={cn(
          'flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-pill border border-line bg-surface px-4',
          'text-[13px] font-semibold text-ink active:scale-95 disabled:opacity-60',
        )}
      >
        {guardando ? <SpinnerGapIcon size={15} className="animate-spin" /> : null}
        Ya lo pagué
      </button>
    </div>
  )
}

/**
 * Monto real de un recibo variable.
 *
 * La luz y el agua no llegan por el mismo importe dos meses seguidos, así que el
 * servidor exige la cifra real. Llega precargada con el estimado —que suele
 * andar cerca— para que corregirla sea teclear dos dígitos y no escribirla
 * entera.
 */
function HojaMontoReal({
  pendiente,
  onCerrar,
}: {
  pendiente: FijoPendiente
  onCerrar: () => void
}) {
  const [montoCents, setMontoCents] = useState(pendiente.montoCents)
  const marcar = useMarcarFijoPagado()

  const guardar = (): void => {
    if (montoCents <= 0) {
      haptic('error')
      return
    }
    marcar.mutate(
      { recurringExpenseId: pendiente.id, amountCents: montoCents },
      {
        onSuccess: () => {
          haptic('success')
          onCerrar()
        },
        // Sin limpiar el monto: volver a teclearlo por un fallo de red es la
        // forma más rápida de que alguien abandone a medio camino.
        onError: () => haptic('error'),
      },
    )
  }

  return (
    <FormSheet
      tinte="peach"
      titulo={pendiente.nombre}
      onCerrar={onCerrar}
      accion={
        <div className="flex flex-col gap-2.5">
          <AvisoError mensaje={marcar.isError ? mensajeDeError(marcar.error) : null} />
          <Guardar onClick={guardar} disabled={montoCents <= 0} guardando={marcar.isPending}>
            Ya lo pagué
          </Guardar>
        </div>
      }
    >
      <Campo
        label="¿De cuánto llegó el recibo?"
        ayuda={`Lo teníamos estimado en ${formatMoney(pendiente.montoCents, { hideZeroCents: true })}.`}
        error={montoCents <= 0 ? 'Escribe el importe real del recibo.' : undefined}
      >
        <Monto cents={montoCents} onChange={setMontoCents} />
      </Campo>
    </FormSheet>
  )
}

/**
 * Esqueleto de carga.
 *
 * Reserva el alto exacto del anillo de 196 px y de las cuatro filas del
 * desglose: si la pantalla se dibujara vacía y luego creciera, el dedo tocaría
 * lo que no era justo cuando llegan los datos.
 */
function Cargando() {
  return (
    <>
      <ScreenHeader title="¿En qué se fue tu dinero?" subtitle="Sacando cuentas…" tinte="peach" />

      <div className="px-safe mx-auto flex max-w-lg flex-col gap-4 pt-1">
        <Card className="flex flex-col items-center gap-4">
          <Skeleton className="size-[196px] rounded-full" />
          <Skeleton className="h-[17px] w-56" />
        </Card>

        <section>
          <div className="mb-3 flex h-[22px] items-center">
            <Skeleton className="h-4 w-52" />
          </div>
          <Card className="divide-y divide-line p-0">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-3">
                <Skeleton className="size-10 shrink-0 rounded-full" />
                <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                  <Skeleton className="h-[14px] w-2/5" />
                  <Skeleton className="h-[12px] w-4/5" />
                </div>
                <Skeleton className="h-[15px] w-16 shrink-0" />
              </div>
            ))}
          </Card>
        </section>
      </div>
    </>
  )
}

/**
 * Si el fijo cambia de importe cada periodo.
 *
 * La justificación no lo dice —su lista trae solo lo mínimo para pintarla— así
 * que se cruza con la lista de fijos. Mientras esa consulta no ha llegado se
 * asume que no varía: en el peor caso el servidor responde pidiendo el monto y
 * el aviso lo explica, que es mejor que retrasar el botón de todos.
 */
function esVariable(
  fijos: ReadonlyArray<{ id: string; isVariableAmount: boolean }> | undefined,
  id: string,
): boolean {
  return fijos?.find((f) => f.id === id)?.isVariableAmount ?? false
}

const MESES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
]

function etiquetaPeriodo(periodo: { inicio: PlainDate; fin: PlainDate }): string {
  const [, mesInicio, diaInicio] = periodo.inicio.split('-')
  const [, mesFin, diaFin] = periodo.fin.split('-')
  const fin = `${Number(diaFin)} de ${MESES[Number(mesFin) - 1]}`
  // Dentro del mismo mes el nombre se dice una vez: «del 1 al 31 de agosto».
  return mesInicio === mesFin
    ? `Del ${Number(diaInicio)} al ${fin}`
    : `Del ${Number(diaInicio)} de ${MESES[Number(mesInicio) - 1]} al ${fin}`
}

/**
 * `differenceInDays` se calcula contra el huso del hogar, no contra el reloj del
 * teléfono: a las 23:30 en México un navegador en UTC ya estaría en el día
 * siguiente y un fijo que vence hoy aparecería como vencido ayer.
 */
function textoVencimiento(dias: number): string {
  if (dias < 0) {
    const atraso = -dias
    return `Venció hace ${atraso} ${atraso === 1 ? 'día' : 'días'}`
  }
  if (dias === 0) return 'Vence hoy'
  if (dias === 1) return 'Vence mañana'
  return `Vence en ${dias} días`
}
